import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getAuth } from 'firebase/auth';
import { Snackbar, Alert, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info'; // Add this to your imports at the top
import { db, doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, addDoc, orderBy, deleteDoc } from '../../firebaseConfig';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Avatar,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
  TextareaAutosize,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WarningIcon from '@mui/icons-material/Warning';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from '../../examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '../../examples/Navbars/DashboardNavbar';
import Footer from '../../examples/Footer';
import { PeriodProvider } from '../../components/PeriodContext';
import { useTheme } from '@mui/material/styles';
import TeamTotalTradesSummary from '../../TeamComponents/TeamTotalTradesSummary';
import TeamWinLossStats from '../../TeamComponents/TeamWinLossStats';
import TeamMostProfitableSymbols from '../../TeamComponents/TeamMostProfitableSymbols';
import TeamTradeAnalytics from '../../TeamComponents/TeamTradeAnalytics';
import TeamWinRateGraph from '../../TeamComponents/TeamWinRateGraph';
import TeamWinRateGraphOverall from '../../TeamComponents/TeamWinRateGraphOverall';
import TeamEquityGraph from '../../TeamComponents/TeamEquityGraph';
import TeamWinRatePieChart from '../../TeamComponents/TeamWinRatePieChart';
import TeamProfitFactorGauge from '../../TeamComponents/TeamProfitFactorGauge';
import TeamProfitFactorGaugeOverall from '../../TeamComponents/TeamProfitFactorGaugeOverall';
import TeamRiskOverTimeChart from '../../TeamComponents/TeamRiskOverTimeChart';
import TeamRRRatioAnalysisChart from '../../TeamComponents/TeamRRRatioAnalysisChart';
import TeamRunningNetPL from '../../TeamComponents/TeamRunningNetPL';
import TeamScoreGraph from '../../TeamComponents/TeamScoreGraph';
import TeamJournalTable from '../../TeamComponents/TeamJournalTable';
import TeamRRRatioTrendOverall from '../../TeamComponents/TeamRRRatioTrendOverall';
import TeamConsistencyScoreOverall from '../../TeamComponents/TeamConsistencyScoreOverall';
import TeamStrategyAdoptionOverall from '../../TeamComponents/TeamStrategyAdoptionOverall';

const TeamDashboard = ({ userID, profileInfo }) => {
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [goalsDialogOpen, setGoalsDialogOpen] = useState(false);
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedTradeAccountID, setSelectedTradeAccountID] = useState('');
  const [tradeAccounts, setTradeAccounts] = useState([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [coachLogsDialogOpen, setCoachLogsDialogOpen] = useState(false);
  const [coachLogs, setCoachLogs] = useState([]);
  const [newLogNote, setNewLogNote] = useState('');
  const [savingLog, setSavingLog] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false); // New state for add member dialog
  const [newMemberEmail, setNewMemberEmail] = useState(''); // State for email input
  const [addMemberError, setAddMemberError] = useState(''); // State for add member errors
  const [editingLogId, setEditingLogId] = useState(null);
  const [editedLogNote, setEditedLogNote] = useState('');
  const [deleteLogDialogOpen, setDeleteLogDialogOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [goals, setGoals] = useState({
    winRate: '',
    maxDrawdown: '',
    minTradesPerMonth: '',
    maxTradesPerMonth: '',
    minRiskRewardRatio: '',
    highConfidenceWinRate: '',
    minTradeScore: '',
    minUniqueSymbols: '',
    maxUniqueSymbols: '',
  });
  const [riskSettings, setRiskSettings] = useState({ stopLoss: '' });
  const [loading, setLoading] = useState(true);
  const [backtestCounts, setBacktestCounts] = useState({});
  const [showCommission, setShowCommission] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [addingMember, setAddingMember] = useState(false); // Added for loading state
  const hasUpdatedRef = useRef(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  // Calculate team average win rate based on raw trades (past 90 days) across all trade accounts
  const calculateTeamWinRate = async (teamId) => {
    try {
      const sevenDaysAgo = new Date(new Date().setDate(new Date().getDate() - 7));
      const teamDataRef = doc(db, 'teamData', teamId);
      const teamDataDoc = await getDoc(teamDataRef);
  
      let teamWinRate = 0;
  
      // Check for cached teamWinRate
      if (teamDataDoc.exists()) {
        const teamData = teamDataDoc.data();
        console.log('TeamDashboard: teamData contents:', teamData);
        if (teamData.lastUpdated?.toDate() >= sevenDaysAgo && teamData.teamWinRate) {
          teamWinRate = parseFloat(teamData.teamWinRate);
          console.log('TeamDashboard: Using cached team win rate:', teamWinRate);
          return teamWinRate.toFixed(2);
        }
      }
  
      // Aggregate monthlyWinRates across all users
      const usersCollectionRef = collection(db, 'teamData', teamId, 'users');
      const usersSnapshot = await getDocs(usersCollectionRef);
      let aggregatedWinRates = [];
      let userCountWithData = 0;
  
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const monthlyWinRates = userData.monthlyWinRates || [];
        console.log(`TeamDashboard: monthlyWinRates for user ${userDoc.id}:`, monthlyWinRates);
  
        const lastThreeMonths = monthlyWinRates.slice(-3);
        if (lastThreeMonths.length > 0) {
          const validWinRates = lastThreeMonths
            .map(rate => parseFloat(rate))
            .filter(rate => !isNaN(rate) && rate > 0);
          if (validWinRates.length > 0) {
            const userAvgWinRate = validWinRates.reduce((sum, rate) => sum + rate, 0) / validWinRates.length;
            aggregatedWinRates.push(userAvgWinRate);
            userCountWithData++;
            console.log(`TeamDashboard: User ${userDoc.id} avg win rate (last 3 months):`, userAvgWinRate);
          }
        }
      }
  
      if (userCountWithData > 0) {
        teamWinRate = aggregatedWinRates.reduce((sum, rate) => sum + rate, 0) / userCountWithData;
        console.log('TeamDashboard: Calculated team win rate:', teamWinRate);
      } else {
        console.log('TeamDashboard: No valid win rates found for team');
      }
  
      // Cache the result
      await setDoc(teamDataRef, {
        teamWinRate,
        lastUpdated: new Date(),
        period: 'past 3 months',
      }, { merge: true });
      console.log(`TeamDashboard: Stored team win rate in teamData/${teamId}:`, teamWinRate);
  
      return teamWinRate.toFixed(2);
    } catch (error) {
      console.error('TeamDashboard: Error calculating team win rate:', error);
      return 0;
    }
  };

   // New function to add a member by email
   const handleAddMember = async () => {
    try {
      setAddMemberError('');
      setAddingMember(true);
  
      if (!newMemberEmail || !newMemberEmail.includes('@')) {
        setAddMemberError('Please enter a valid email address');
        console.log('TeamDashboard: Invalid email format:', newMemberEmail);
        return;
      }
  
      // Helper function to query user by email
      const findUserByEmail = async (email) => {
        console.log('TeamDashboard: Querying email:', email);
        const usersQuery = query(
          collection(db, 'Users'),
          where('email', '==', email)
        );
        return await getDocs(usersQuery);
      };
  
      // Try the email as entered
      let userDocs = await findUserByEmail(newMemberEmail);
  
      // If no user found, try with first letter capitalized
      if (userDocs.empty) {
        const capitalFirstEmail =
          newMemberEmail.charAt(0).toUpperCase() + newMemberEmail.slice(1);
        if (capitalFirstEmail !== newMemberEmail) { // Only retry if the email changed
          console.log(
            `TeamDashboard: No user found for ${newMemberEmail}, trying ${capitalFirstEmail}`
          );
          userDocs = await findUserByEmail(capitalFirstEmail);
        }
      }
  
      if (userDocs.empty) {
        setAddMemberError('No user found with this email address');
        console.log('TeamDashboard: No user found for either email variation');
        return;
      }
  
      const userDoc = userDocs.docs[0];
      const userId = userDoc.id;
      const userData = userDoc.data();
      console.log('TeamDashboard: Found user:', userId, userData.email);
  
      // Check if user is already in the team
      if (userData.profileInfo?.teamId === team.id) {
        setAddMemberError('This user is already a member of the team');
        console.log('TeamDashboard: User already in team:', userId);
        return;
      }
  
      // Update user's profile to join the team
      console.log('TeamDashboard: Adding user to team:', userId);
      await updateDoc(doc(db, 'Users', userId), {
        'profileInfo.teamId': team.id,
        'profileInfo.teamRole': 'member',
        [`profileInfo.teamPermissions.${team.id}`]: false,
      });
  
      // Update team members list
      const updatedMembers = [...team.members, userId];
      const newCommissionRate = calculateTeamCommissionRate(updatedMembers.length);
      await updateDoc(doc(db, 'teams', team.id), {
        members: updatedMembers,
        commissionRate: newCommissionRate,
        updatedAt: new Date(),
      });
  
      // Trigger permission modal for the new member
      sessionStorage.setItem('pendingTeamPermission', JSON.stringify({
        userId: userId,
        teamId: team.id,
      }));
  
      // Update local state
      setTeam({ ...team, members: updatedMembers, commissionRate: newCommissionRate });
      setMembers([
        ...members,
        {
          id: userId,
          ...userData.profileInfo,
          teamId: team.id,
          teamRole: 'member',
          teamPermissions: { [team.id]: false },
          isFlagged: false,
          unmetGoals: [],
        },
      ]);
  
      console.log('TeamDashboard: Added member:', userId);
      setSnackbarMessage('Member added successfully!');
      setSnackbarOpen(true);
      setAddMemberDialogOpen(false);
      setNewMemberEmail('');
    } catch (error) {
      console.error('TeamDashboard: Error adding member:', error);
      setAddMemberError('Failed to add member. Please try again.');
    } finally {
      setAddingMember(false);
    }
  };

  useEffect(() => {
    const fetchTeamData = async () => {
      console.log('TeamDashboard: profileInfo=', profileInfo);
      console.log('TeamDashboard: isAdmin=', profileInfo.role === 'admin');
      console.log('TeamDashboard: isAuthorizedRole=', ['coach', 'co-coach'].includes(profileInfo.role || profileInfo.teamRole));
      console.log('TeamDashboard: teamId=', profileInfo.teamId);
      console.log('TeamDashboard: profileInfo.affiliateCode=', profileInfo.affiliateCode);
  
      const isAdmin = profileInfo.role === 'admin';
      const isAuthorizedRole = ['coach', 'co-coach'].includes(profileInfo.role || profileInfo.teamRole);
      let effectiveProfileInfo = profileInfo ? { ...profileInfo } : {};
  
      if (isAdmin && !profileInfo.teamId) {
        const userDoc = await getDoc(doc(db, 'Users', userID));
        if (userDoc.exists()) {
          effectiveProfileInfo = {
            ...profileInfo,
            ...userDoc.data().profileInfo,
            teamId: userDoc.data().profileInfo?.teamId || null,
            teamRole: userDoc.data().profileInfo?.teamRole || profileInfo.teamRole,
          };
          console.log('TeamDashboard: Fetched latest profileInfo=', effectiveProfileInfo);
        }
      }
  
      if (effectiveProfileInfo.teamRole === 'coach' && effectiveProfileInfo.teamId) {
        const userRef = doc(db, 'Users', userID);
        const userDoc = await getDoc(userRef);
        const currentPermissions = userDoc.exists() ? userDoc.data().profileInfo?.teamPermissions || {} : {};
        if (!currentPermissions[effectiveProfileInfo.teamId]) {
          await updateDoc(userRef, {
            [`profileInfo.teamPermissions.${effectiveProfileInfo.teamId}`]: true
          });
          console.log('TeamDashboard: Set coach teamPermissions to true for team:', effectiveProfileInfo.teamId);
        }
        effectiveProfileInfo.teamPermissions = {
          ...currentPermissions,
          [effectiveProfileInfo.teamId]: true
        };
      }
  
      if (!isAdmin && (!effectiveProfileInfo.teamId || !isAuthorizedRole)) {
        console.log('TeamDashboard: Unauthorized access, stopping fetch');
        setLoading(false);
        return;
      }
  
      try {
        let teamRef, teamDoc, teamData;
        let inviteCode = profileInfo.affiliateCode;
  
        if (!inviteCode && effectiveProfileInfo.teamId) {
          const discountQuery = query(
            collection(db, 'discountCodes'),
            where('affiliateId', '==', userID),
            where('isCoach', '==', true)
          );
          const discountDocs = await getDocs(discountQuery);
          if (!discountDocs.empty) {
            inviteCode = discountDocs.docs[0].data().code;
            console.log('TeamDashboard: Fetched inviteCode from discountCodes:', inviteCode);
          } else {
            console.warn('TeamDashboard: No discount code found for coach:', userID);
          }
        }
  
        if (effectiveProfileInfo.teamId) {
          teamRef = doc(db, 'teams', effectiveProfileInfo.teamId);
          teamDoc = await getDoc(teamRef);
          if (teamDoc.exists()) {
            teamData = { id: teamDoc.id, ...teamDoc.data() };
            if (teamData.inviteCode !== inviteCode && inviteCode) {
              await updateDoc(teamRef, { inviteCode: inviteCode.toUpperCase(), updatedAt: new Date() });
              teamData.inviteCode = inviteCode.toUpperCase();
              console.log('TeamDashboard: Updated team inviteCode to match discount code:', inviteCode);
            } else if (!teamData.inviteCode && inviteCode) {
              await updateDoc(teamRef, { inviteCode: inviteCode.toUpperCase(), updatedAt: new Date() });
              teamData.inviteCode = inviteCode.toUpperCase();
              console.log('TeamDashboard: Set team inviteCode from discount code:', inviteCode);
            }
            console.log('TeamDashboard: Team fetched from Firestore:', teamData);
          } else {
            if (!inviteCode) {
              console.error('TeamDashboard: No discount code available for new team creation');
              setLoading(false);
              return;
            }
            const newTeam = {
              name: `Team ${effectiveProfileInfo.fullName || 'Coach'}`,
              coachId: userID,
              members: [userID],
              inviteCode: inviteCode.toUpperCase(),
              commissionRate: calculateTeamCommissionRate(1),
              createdAt: new Date(),
              updatedAt: new Date(),
            };
            await setDoc(teamRef, newTeam);
            teamData = { id: effectiveProfileInfo.teamId, ...newTeam };
            console.log('TeamDashboard: New team created:', teamData);
          }
        } else if (isAdmin) {
          teamData = {
            id: 'admin-placeholder',
            name: `Admin Team ${effectiveProfileInfo.fullName || 'Admin'}`,
            coachId: userID,
            members: [],
            inviteCode: 'N/A',
            commissionRate: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          console.log('TeamDashboard: Admin placeholder team initialized:', teamData);
        }
  
        // Fetch members before setting team state
        let memberData = [];
        let memberIds = [];
        if (effectiveProfileInfo.teamId) {
          const membersQuery = query(
            collection(db, 'Users'),
            where('profileInfo.teamId', '==', effectiveProfileInfo.teamId)
          );
          const memberDocs = await getDocs(membersQuery);
          const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30));
  
          memberData = await Promise.all(
            memberDocs.docs.map(async (doc) => {
              const userData = { id: doc.id, ...doc.data().profileInfo };
              memberIds.push(doc.id);
              const goals = userData.coachGoals?.[effectiveProfileInfo.teamId];
              const riskSettings = userData.coachRiskSettings?.[effectiveProfileInfo.teamId];
  
              const accountsQuery = query(collection(db, `Users/${doc.id}/tradeAccounts`));
              const accountsDocs = await getDocs(accountsQuery);
              const tradeAccountIds = [...new Set(accountsDocs.docs.map((accountDoc) => accountDoc.id))]; // Remove duplicates
  
              // Aggregate trades across all accounts
              let totalWins = 0;
              let totalValidTrades = 0;
              let totalTradesThisMonth = 0;
              let totalStopLoss = 0;
              let stopLossTradeCount = 0;
              let maxDrawdown = 0;
              let totalCapital = 0;
              const allTrades = [];
              const unmetGoals = [];
              const thirtyDaysAgoString = thirtyDaysAgo.toISOString().split('T')[0];
  
              // Calculate total capital and collect trades
              for (const tradeAccountId of tradeAccountIds) {
                const capitalPath = `Users/${doc.id}/tradeAccounts/${tradeAccountId}/capital`;
                const capitalQuery = query(collection(db, capitalPath));
                const capitalDocs = await getDocs(capitalQuery);
                let deposits = 0;
                let withdrawals = 0;
                let returns = 0;
  
                capitalDocs.forEach((capitalDoc) => {
                  const data = capitalDoc.data();
                  deposits += Number(data.deposit) || 0;
                  withdrawals += Number(data.withdrawal) || 0;
                });
  
                const tradesQuery = query(
                  collection(db, `Users/${doc.id}/tradeAccounts/${tradeAccountId}/trades`),
                  where('date', '>=', thirtyDaysAgoString)
                );
                const tradesDocs = await getDocs(tradesQuery);
                if (!tradesDocs.empty) {
                  const trades = tradesDocs.docs.map((t) => t.data());
                  const validTrades = trades.filter((t) => t.returns !== undefined && t.status === "Closed");
                  allTrades.push(...validTrades);
                  returns = validTrades.reduce((sum, trade) => sum + (Number(trade.returns) || 0), 0);
                }
  
                totalCapital += deposits - withdrawals + returns;
              }
  
              if (totalCapital <= 0) {
                console.log(`TeamDashboard: No capital for ${doc.id}, estimating from trades`);
                totalCapital = allTrades.reduce((sum, trade) => {
                  return sum + (Number(trade.returns) || 0) + (Number(trade.riskAmount) || 0);
                }, 0) || 1000;
              }
  
              // Process trades
              const validTrades = allTrades;
              totalWins = validTrades.filter((t) => Number(t.returns) > 0).length;
              totalValidTrades = validTrades.length;
              totalTradesThisMonth = validTrades.length;
  
              const tradesWithRisk = validTrades.filter((t) => t.riskAmount !== undefined && t.riskAmount !== null);
              const accountRiskSum = tradesWithRisk.reduce((sum, trade) => {
                const riskAmount = parseFloat(trade.riskAmount) || 0;
                return sum + (totalCapital > 0 ? (riskAmount / totalCapital) * 100 : 0);
              }, 0);
              totalStopLoss += accountRiskSum;
              stopLossTradeCount += tradesWithRisk.length;
  
              // Calculate max drawdown
              if (allTrades.length > 0) {
                allTrades.sort((a, b) => new Date(a.date + 'T' + a.entryTime) - new Date(b.date + 'T' + b.entryTime));
                let equity = totalCapital || 1000;
                let peak = equity;
                let trough = equity;
  
                for (const trade of allTrades) {
                  equity += Number(trade.returns) || 0;
                  peak = Math.max(peak, equity);
                  trough = Math.min(trough, equity);
                  const drawdown = peak > 0 ? ((peak - trough) / peak) * 100 : 0;
                  maxDrawdown = Math.max(maxDrawdown, drawdown);
                }
              }
  
              const avgWinRate = totalValidTrades > 0 ? (totalWins / totalValidTrades) * 100 : 0;
              const avgStopLoss = stopLossTradeCount > 0 ? totalStopLoss / stopLossTradeCount : 0;
  
              // Evaluate goals
              if (goals?.winRate && avgWinRate < goals.winRate) {
                unmetGoals.push(`Win Rate: ${avgWinRate.toFixed(1)}% < ${goals.winRate}%`);
              }
              if (goals?.maxDrawdown && maxDrawdown > goals.maxDrawdown) {
                unmetGoals.push(`Max Drawdown: ${maxDrawdown.toFixed(1)}% > ${goals.maxDrawdown}%`);
              }
              if (goals?.minTradesPerMonth && totalTradesThisMonth < goals.minTradesPerMonth) {
                unmetGoals.push(`Trades/Month: ${totalTradesThisMonth} < ${goals.minTradesPerMonth}`);
              }
              if (goals?.maxTradesPerMonth && totalTradesThisMonth > goals.maxTradesPerMonth) {
                unmetGoals.push(`Trades/Month: ${totalTradesThisMonth} > ${goals.maxTradesPerMonth}`);
              }
              if (riskSettings?.stopLoss && avgStopLoss > riskSettings.stopLoss) {
                unmetGoals.push(`Stop Loss: ${avgStopLoss.toFixed(1)}% > ${riskSettings.stopLoss}%`);
              }
  
              // New goals
              const tradesWithRR = validTrades.filter(t => t.riskAmount && t.returns !== undefined && t.status === "Closed");
              const rrRatios = tradesWithRR
                .filter(t => t.returns > 0 && t.riskAmount > 0)
                .map(t => t.returns / t.riskAmount);
              const avgRRRatio = rrRatios.length > 0 ? rrRatios.reduce((sum, r) => sum + r, 0) / rrRatios.length : 0;
              if (goals?.minRiskRewardRatio && avgRRRatio < goals.minRiskRewardRatio) {
                unmetGoals.push(`Risk-to-Reward Ratio: ${avgRRRatio.toFixed(2)} < ${goals.minRiskRewardRatio}`);
              }
  
              const highConfidenceTrades = validTrades.filter(t => t.tradeConfidence === 'High');
              const highConfidenceWins = highConfidenceTrades.filter(t => t.returns > 0).length;
              const highConfidenceWinRate = highConfidenceTrades.length > 0 ? (highConfidenceWins / highConfidenceTrades.length) * 100 : 0;
              if (goals?.highConfidenceWinRate && highConfidenceWinRate < goals.highConfidenceWinRate) {
                unmetGoals.push(`High-Confidence Win Rate: ${highConfidenceWinRate.toFixed(1)}% < ${goals.highConfidenceWinRate}%`);
              }
  
              const tradeScores = validTrades
                .map(t => t.fxbTradeScore ? parseInt(t.fxbTradeScore.split('/')[0]) : 0)
                .filter(score => score > 0);
              const avgTradeScore = tradeScores.length > 0 ? tradeScores.reduce((sum, s) => sum + s, 0) / tradeScores.length : 0;
              if (goals?.minTradeScore && avgTradeScore < goals.minTradeScore) {
                unmetGoals.push(`Trade Score: ${avgTradeScore.toFixed(1)} < ${goals.minTradeScore}`);
              }
  
              const uniqueSymbols = [...new Set(validTrades.map(t => t.symbol))];
              if (goals?.minUniqueSymbols && uniqueSymbols.length < goals.minUniqueSymbols) {
                unmetGoals.push(`Unique Symbols: ${uniqueSymbols.length} < ${goals.minUniqueSymbols}`);
              }
              if (goals?.maxUniqueSymbols && uniqueSymbols.length > goals.maxUniqueSymbols) {
                unmetGoals.push(`Unique Symbols: ${uniqueSymbols.length} > ${goals.maxUniqueSymbols}`);
              }
  
              const isFlagged = unmetGoals.length > 0;
  
              console.log('TeamDashboard: Member:', userData.id, {
                avgWinRate,
                maxDrawdown,
                totalTradesThisMonth,
                avgStopLoss,
                totalCapital,
                avgRRRatio,
                highConfidenceWinRate,
                avgTradeScore,
                uniqueSymbols: uniqueSymbols.length,
                isFlagged,
                unmetGoals
              });
  
              return { ...userData, isFlagged, unmetGoals };
            })
          );
  
          console.log('TeamDashboard: Members fetched:', memberData);
          setMembers(memberData);
          // Update team.members with fetched member IDs
          teamData.members = memberIds;
          console.log('TeamDashboard: Updated team.members:', teamData.members);
          // Persist members to Firestore
          await updateDoc(teamRef, {
            members: memberIds,
            updatedAt: new Date(),
          });
        }
  
        // Calculate win rate using monthlyWinRates
        let teamWinRate = 0;
        if (teamData.id !== 'admin-placeholder') {
          teamWinRate = await calculateTeamWinRate(teamData.id); // Pass team ID
          console.log('TeamDashboard: Team win rate fetched:', teamWinRate);
        }
  
        teamData.teamWinRate = teamWinRate;
        setTeam(teamData);
        setNewTeamName(teamData.name);
        console.log('TeamDashboard: team state updated:', teamData);
  
        if (teamData.id !== 'admin-placeholder') {
          const logoDocRef = doc(db, 'teamLogos', teamData.id);
          const logoDoc = await getDoc(logoDocRef);
          if (logoDoc.exists()) {
            setLogoUrl(logoDoc.data().logoUrl || '');
            console.log('TeamDashboard: Logo URL fetched:', logoDoc.data().logoUrl);
          }
        }
  
        if (teamData.id !== 'admin-placeholder') {
          const newCommissionRate = calculateTeamCommissionRate(memberData.length);
          if (newCommissionRate !== teamData.commissionRate) {
            await updateDoc(teamRef, {
              commissionRate: newCommissionRate,
              updatedAt: new Date(),
            });
            teamData.commissionRate = newCommissionRate;
            setTeam({ ...teamData });
            console.log('TeamDashboard: Commission rate updated:', newCommissionRate);
          }
        }
  
        const backtestCountsData = {};
        await Promise.all(
          memberData.map(async (member) => {
            const backtestsQuery = query(
              collection(db, `Users/${member.id}/backtesting`)
            );
            const backtestsDocs = await getDocs(backtestsQuery);
            backtestCountsData[member.id] = backtestsDocs.size;
            console.log(`TeamDashboard: Backtest count for ${member.id}: ${backtestsDocs.size}`);
          })
        );
        console.log('TeamDashboard: Backtest counts:', backtestCountsData);
        setBacktestCounts(backtestCountsData);
      } catch (error) {
        console.error('TeamDashboard: Error fetching team data:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchTeamData();
  }, [profileInfo, userID]);

// Update the fetchCoachLogs function to ensure proper timestamp conversion
const fetchCoachLogs = async (memberId, teamId) => {
    setLogsLoading(true);
    try {
      console.log(`Fetching coach logs for member=${memberId}, team=${teamId}`);
      const logsQuery = query(
        collection(db, `Users/${memberId}/coachLogs/${teamId}/logs`),
        orderBy('createdAt', 'desc')
      );
      const logsSnapshot = await getDocs(logsQuery);
      const logsData = logsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
        };
      });
      console.log(`TeamDashboard: Fetched ${logsData.length} coach logs for member ${memberId}:`, logsData);
      setCoachLogs(logsData);
    } catch (error) {
      console.error('TeamDashboard: Error fetching coach logs:', error.message, error.code);
      setSnackbarMessage(`Failed to fetch coach logs: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSaveCoachLog = async () => {
    if (!newLogNote.trim()) {
      setSnackbarMessage('Log note cannot be empty');
      setSnackbarOpen(true);
      return;
    }
  
    setSavingLog(true);
    try {
      const logRef = collection(db, `Users/${selectedMember.id}/coachLogs/${team.id}/logs`);
      const newLog = {
        note: newLogNote,
        createdBy: userID,
        createdAt: new Date(),
        teamId: team.id,
      };
      const docRef = await addDoc(logRef, newLog);
      setCoachLogs([{ id: docRef.id, ...newLog, createdAt: new Date() }, ...coachLogs]);
      setNewLogNote('');
      setSnackbarMessage('Coach log saved successfully');
      setSnackbarOpen(true);
      console.log(`TeamDashboard: Saved coach log for member ${selectedMember.id}:`, newLog);
    } catch (error) {
      console.error('TeamDashboard: Error saving coach log:', error);
      setSnackbarMessage('Failed to save coach log');
      setSnackbarOpen(true);
    } finally {
      setSavingLog(false);
    }
  };

// Add these handlers within the TeamDashboard component, below the existing handlers
const handleEditCoachLog = (logId, currentNote) => {
    setEditingLogId(logId);
    setEditedLogNote(currentNote);
    console.log(`TeamDashboard: Editing coach log ${logId}`);
  };
  
  const handleSaveEditedCoachLog = async (logId) => {
    if (!editedLogNote.trim()) {
      setSnackbarMessage('Log note cannot be empty');
      setSnackbarOpen(true);
      return;
    }
  
    setSavingLog(true);
    try {
      const logRef = doc(db, `Users/${selectedMember.id}/coachLogs/${team.id}/logs`, logId);
      const updatedAt = new Date();
      await updateDoc(logRef, {
        note: editedLogNote,
        updatedAt: updatedAt,
        updatedBy: userID,
      });
      setCoachLogs(
        coachLogs.map((log) =>
          log.id === logId
            ? { ...log, note: editedLogNote, updatedAt: updatedAt, updatedBy: userID }
            : log
        )
      );
      setEditingLogId(null);
      setEditedLogNote('');
      setSnackbarMessage('Coach log updated successfully');
      setSnackbarOpen(true);
      console.log(`TeamDashboard: Updated coach log ${logId} with updatedAt: ${updatedAt}`);
    } catch (error) {
      console.error('TeamDashboard: Error updating coach log:', error);
      setSnackbarMessage('Failed to update coach log');
      setSnackbarOpen(true);
    } finally {
      setSavingLog(false);
    }
  };

  const handleOpenDeleteLogDialog = (logId) => {
    setLogToDelete(logId);
    setDeleteLogDialogOpen(true);
    console.log(`TeamDashboard: Opened delete confirmation for log ${logId}`);
  };
  
 const handleDeleteCoachLog = async () => {
  if (!logToDelete) return;

  try {
    const logRef = doc(db, `Users/${selectedMember.id}/coachLogs/${team.id}/logs`, logToDelete);
    await deleteDoc(logRef);
    setCoachLogs(coachLogs.filter((log) => log.id !== logToDelete));
    setSnackbarMessage('Coach log deleted successfully');
    setSnackbarOpen(true);
    console.log(`TeamDashboard: Deleted coach log ${logToDelete}`);
  } catch (error) {
    console.error('TeamDashboard: Error deleting coach log:', error);
    setSnackbarMessage('Failed to delete coach log');
    setSnackbarOpen(true);
  } finally {
    setDeleteLogDialogOpen(false);
    setLogToDelete(null);
  }
};

  useEffect(() => {
    if (!team || team.id === 'admin-placeholder') return;

    const fetchLogoUrl = async () => {
      try {
        const logoDocRef = doc(db, 'teamLogos', team.id);
        const logoDoc = await getDoc(logoDocRef);
        if (logoDoc.exists()) {
          const data = logoDoc.data();
          console.log('TeamDashboard: Fetched logo URL:', data.logoUrl);
          setLogoUrl(data.logoUrl);
        } else {
          console.log('TeamDashboard: No logo found for team:', team.id);
        }
      } catch (error) {
        console.error('TeamDashboard: Error fetching logo URL:', error);
      }
    };

    fetchLogoUrl();
  }, [team]);

  useEffect(() => {
    const updateLastAccessed = async () => {
      if (tabValue === 0 && team && team.id !== 'admin-placeholder' && !hasUpdatedRef.current) {
        try {
          hasUpdatedRef.current = true;
          const teamRef = doc(db, 'teams', team.id);
          const updatedAt = new Date();
          await updateDoc(teamRef, { updatedAt });
          setTeam((prev) => ({ ...prev, updatedAt }));
          console.log('TeamDashboard: Updated team.updatedAt:', updatedAt);
        } catch (error) {
          console.error('TeamDashboard: Error updating last accessed time:', error);
        }
      } else if (tabValue !== 0) {
        hasUpdatedRef.current = false;
      }
    };

    updateLastAccessed();
  }, [tabValue, team]);

  

  useEffect(() => {
    const fetchTradeAccounts = async () => {
      if (!selectedMember) {
        setTradeAccounts([]);
        setSelectedTradeAccountID('');
        return;
      }

      try {
        const accountsQuery = query(
          collection(db, `Users/${selectedMember.id}/tradeAccounts`)
        );
        const accountsDocs = await getDocs(accountsQuery);
        const accountsData = accountsDocs.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name || `Account ${doc.id}`,
        }));
        console.log('TeamDashboard: Trade accounts fetched for member:', selectedMember.id, accountsData);
        setTradeAccounts(accountsData);
        setSelectedTradeAccountID(accountsData.length > 0 ? accountsData[0].id : '');
      } catch (error) {
        console.error('TeamDashboard: Error fetching trade accounts:', error);
        setTradeAccounts([]);
        setSelectedTradeAccountID('');
      }
    };

    fetchTradeAccounts();
  }, [selectedMember]);

  const handleTabChange = (event, newValue) => {
    if (newValue !== 2) {
      setSelectedMember(null);
      setSelectedTradeAccountID('');
      setTradeAccounts([]);
      console.log('TeamDashboard: Cleared selected member and trade accounts on tab change');
    }
    setTabValue(newValue);
  };

  const handleUpdateSettings = async () => {
    try {
      if (team.id !== 'admin-placeholder') {
        await updateDoc(doc(db, 'teams', team.id), {
          name: newTeamName,
          updatedAt: new Date(),
        });
        setTeam({ ...team, name: newTeamName });
        console.log('TeamDashboard: Team settings updated:', { name: newTeamName });
      }
      setSettingsDialogOpen(false);
    } catch (error) {
      console.error('TeamDashboard: Error updating team settings:', error);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !team || team.id === 'admin-placeholder') return;

    setLogoFile(file);

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      console.error('TeamDashboard: No authenticated user found');
      return;
    }

    console.log('TeamDashboard: User UID:', user.uid);

    try {
      const userDocRef = doc(db, 'Users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('TeamDashboard: Full user document:', userData);
        console.log('TeamDashboard: profileInfo.teamId:', userData.profileInfo.teamId);
        console.log('TeamDashboard: profileInfo.teamRole:', userData.profileInfo.teamRole);
        console.log('TeamDashboard: Expected teamId:', team.id);
      } else {
        console.error('TeamDashboard: User document does not exist');
        return;
      }
    } catch (error) {
      console.error('TeamDashboard: Error fetching user document:', error);
      return;
    }

    const storage = getStorage();
    const storageRef = ref(storage, `teamLogos/${team.id}/${file.name}`);

    try {
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('TeamDashboard: Logo uploaded to Storage, URL:', downloadURL);

      const logoDocRef = doc(db, 'teamLogos', team.id);
      await setDoc(logoDocRef, {
        logoUrl: downloadURL,
        teamId: team.id,
        uploadedAt: new Date(),
      }, { merge: true });

      setLogoUrl(downloadURL);
      console.log('TeamDashboard: Logo URL saved to Firestore in teamLogos collection');
    } catch (error) {
      console.error('TeamDashboard: Error uploading logo:', error);
    }
  };

  const handleSetGoals = async () => {
    try {
      await updateDoc(doc(db, 'Users', selectedMember.id), {
        'profileInfo.coachGoals': {
          [team.id]: {
            winRate: Number(goals.winRate),
            maxDrawdown: Number(goals.maxDrawdown),
            minTradesPerMonth: Number(goals.minTradesPerMonth),
            setBy: userID,
            setAt: new Date(),
          },
        },
      });
      setMembers(
        members.map((m) =>
          m.id === selectedMember.id
            ? {
                ...m,
                coachGoals: {
                  ...m.coachGoals,
                  [team.id]: {
                    winRate: Number(goals.winRate),
                    maxDrawdown: Number(goals.maxDrawdown),
                    minTradesPerMonth: Number(goals.minTradesPerMonth),
                    setBy: userID,
                    setAt: new Date(),
                  },
                },
              }
            : m
        )
      );
      console.log('TeamDashboard: Goals set for member:', selectedMember.id);
      setGoals({ winRate: '', maxDrawdown: '', minTradesPerMonth: '' });
      setGoalsDialogOpen(false);
    } catch (error) {
      console.error('TeamDashboard: Error setting goals:', error);
    }
  };

  const handleSetRiskSettings = async () => {
    try {
      await updateDoc(doc(db, 'Users', selectedMember.id), {
        'profileInfo.coachRiskSettings': {
          [team.id]: {
            stopLoss: Number(riskSettings.stopLoss),
            setBy: userID,
            setAt: new Date(),
          },
        },
      });
      setMembers(
        members.map((m) =>
          m.id === selectedMember.id
            ? {
                ...m,
                coachRiskSettings: {
                  ...m.coachRiskSettings,
                  [team.id]: {
                    stopLoss: Number(riskSettings.stopLoss),
                    setBy: userID,
                    setAt: new Date(),
                  },
                },
              }
            : m
        )
      );
      console.log('TeamDashboard: Risk settings set for member:', selectedMember.id);
      setRiskSettings({ stopLoss: '' });
      setRiskDialogOpen(false);
    } catch (error) {
      console.error('TeamDashboard: Error setting risk settings:', error);
    }
  };

  const handleRemoveMember = async () => {
    try {
      await updateDoc(doc(db, 'Users', selectedMember.id), {
        'profileInfo.teamId': null,
        'profileInfo.teamPermissions': { [team.id]: false },
        'profileInfo.teamRole': 'member',
      });
      await updateDoc(doc(db, 'subscriptions', selectedMember.id), {
        plan: 'monthly_plan',
        'planDetails.cost': 19.99,
        'planDetails.discountApplied': null,
        updatedAt: new Date(),
      });
      if (team.id !== 'admin-placeholder') {
        const updatedMembers = team.members.filter((id) => id !== selectedMember.id);
        const newCommissionRate = calculateTeamCommissionRate(updatedMembers.length);
        await updateDoc(doc(db, 'teams', team.id), {
          members: updatedMembers,
          commissionRate: newCommissionRate,
          updatedAt: new Date(),
        });
        setTeam({ ...team, members: updatedMembers, commissionRate: newCommissionRate });
      }
      setMembers(members.filter((m) => m.id !== selectedMember.id));
      console.log('TeamDashboard: Member removed:', selectedMember.id);
      setRemoveDialogOpen(false);
      setSelectedMember(null);
      setSelectedTradeAccountID('');
      setTradeAccounts([]);
    } catch (error) {
      console.error('TeamDashboard: Error removing member:', error);
    }
  };

  const handlePromoteCoCoach = async (memberId) => {
    try {
      await updateDoc(doc(db, 'Users', memberId), {
        'profileInfo.teamRole': 'co-coach',
      });
      setMembers(
        members.map((m) => (m.id === memberId ? { ...m, teamRole: 'co-coach' } : m))
      );
      console.log('TeamDashboard: Member promoted to co-coach:', memberId);
    } catch (error) {
      console.error('TeamDashboard: Error promoting co-coach:', error);
    }
  };

  const handleDemoteMember = async (memberId) => {
    try {
      await updateDoc(doc(db, 'Users', memberId), {
        'profileInfo.teamRole': 'member',
      });
      setMembers(
        members.map((m) => (m.id === memberId ? { ...m, teamRole: 'member' } : m))
      );
      console.log('TeamDashboard: Member demoted to member:', memberId);
    } catch (error) {
      console.error('TeamDashboard: Error demoting member:', error);
    }
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setTabValue(2);
    console.log('TeamDashboard: Member selected for metrics:', member.id);
  };

  const handleTradeAccountChange = (event) => {
    setSelectedTradeAccountID(event.target.value);
    console.log('TeamDashboard: Trade account selected:', event.target.value);
  };

  const calculateTeamCommissionRate = (memberCount) => {
    if (memberCount <= 50) {
      return 20;
    } else if (memberCount <= 100) {
      return 30;
    } else {
      return 40;
    }
  };

  const toggleCommissionVisibility = () => {
    setShowCommission((prev) => !prev);
  };

  // Memoize permittedUserIDs to prevent Metrics tab re-renders
  const permittedUserIDs = useMemo(() => {
    return members
      .filter((m) => m.teamPermissions?.[team?.id])
      .map((m) => m.id);
  }, [members, team?.id]);

  // Flagged Members Card Component
  const FlaggedMembersCard = ({ achievingCount, notAchievingCount }) => (
    <Card sx={{ minHeight: 130 }}>
      <CardContent>
        <MDBox display="flex" alignItems="center">
          <WarningIcon color="error" sx={{ mr: 1 }} />
          <MDTypography variant="h6">Goal Status</MDTypography>
        </MDBox>
        <MDTypography variant="body2" sx={{ mt: 1 }}>
          Members achieving goals: <MDTypography component="span" color="success" fontWeight="medium">{achievingCount}</MDTypography>
        </MDTypography>
        <MDTypography variant="body2">
          Members not achieving goals: <MDTypography component="span" color="warning" fontWeight="medium">{notAchievingCount}</MDTypography>
        </MDTypography>
      </CardContent>
    </Card>
  );

  // Team Win Rate Card Component
  const TeamWinRateCard = ({ winRate }) => (
    <Card sx={{ minHeight: 130 }}>
      <CardContent>
        <MDTypography variant="h6">Team Average Win Rate</MDTypography>
        <MDTypography variant="h4" color="success">
          {winRate}%
        </MDTypography>
        <MDTypography variant="caption" sx={{ mt: 1 }}>
          Team win rate updates weekly. Based on the average of the past 3 months.
        </MDTypography>
      </CardContent>
    </Card>
  );

  if (loading || !team) {
    console.log('TeamDashboard: Rendering loading state, team=', team);
    return (
      <DashboardLayout>
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  console.log('TeamDashboard: Rendering main content, team=', team);

  const notAchievingCount = members.filter((m) => m.isFlagged).length;
  const achievingCount = members.length - notAchievingCount;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PeriodProvider>
        <MDBox py={3}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Overview" />
            <Tab label="Members" />
            <Tab label="Metrics" />
            <Tab label="Settings" />
          </Tabs>

          {/* Overview Tab */}
          {tabValue === 0 && (
            <Grid container spacing={3} mt={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<MDTypography variant="h6">Team Overview</MDTypography>} />
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <MDBox>
                          <MDTypography variant="body1" gutterBottom>
                            Name: {team.name}
                          </MDTypography>
                          <MDTypography variant="body1" gutterBottom>
                            Members: {team.members.length}
                          </MDTypography>
                          <MDTypography variant="body1" gutterBottom>
                            Invite Code: {team.inviteCode}
                          </MDTypography>
                          <MDBox display="flex" alignItems="center">
                            <MDTypography variant="body1" gutterBottom mr={1}>
                              Commission Rate: {showCommission ? `${team.commissionRate}%` : '••••'}
                            </MDTypography>
                            <IconButton onClick={toggleCommissionVisibility} size="small" style={{ color: iconColor }}>
                              {showCommission ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </MDBox>
                        </MDBox>
                      </Grid>
                      {logoUrl && (
                        <Grid item xs={12} md={4} display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                          <MDBox mr={50}>
                            <Avatar src={logoUrl} alt="Team Logo" sx={{ width: 200, height: 200 }} />
                          </MDBox>
                        </Grid>
                      )}
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <MDTypography variant="body1">
                      Last Updated: {team.updatedAt ? new Date(team.updatedAt).toLocaleString() : 'N/A'}
                    </MDTypography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <FlaggedMembersCard
                  achievingCount={achievingCount}
                  notAchievingCount={notAchievingCount}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TeamWinRateCard winRate={team.teamWinRate || 0} />
              </Grid>
            </Grid>
          )}

          {/* Members Tab (modified) */}
          {tabValue === 1 && (
            <Grid container spacing={3} mt={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title={<MDTypography variant="h6">Team Members</MDTypography>}
                    action={
                      ['coach', 'co-coach'].includes(profileInfo.teamRole) && (
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={() => setAddMemberDialogOpen(true)}
                        >
                          Add Member
                        </MDButton>
                      )
                    }
                  />
                  <CardContent>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th
                            style={{
                              backgroundColor: theme.palette.background.default || '#bac7db',
                              borderTopLeftRadius: '12px',
                              borderBottomLeftRadius: '12px',
                              padding: '12px 16px',
                              width: '150px',
                              textAlign: 'center',
                            }}
                          >
                            <MDTypography variant="h6" component="span">
                              Name
                            </MDTypography>
                          </th>
                          <th
                            style={{
                              backgroundColor: theme.palette.background.default || '#bac7db',
                              padding: '12px 16px',
                              width: '200px',
                              textAlign: 'center',
                            }}
                          >
                            <MDTypography variant="h6" component="span">
                              Email
                            </MDTypography>
                          </th>
                          <th
                            style={{
                              backgroundColor: theme.palette.background.default || '#bac7db',
                              padding: '12px 16px',
                              width: '100px',
                              textAlign: 'center',
                            }}
                          >
                            <MDTypography variant="h6" component="span">
                              Role
                            </MDTypography>
                          </th>
                          <th
                            style={{
                              backgroundColor: theme.palette.background.default || '#bac7db',
                              padding: '12px 16px',
                              width: '120px',
                              textAlign: 'center',
                            }}
                          >
                            <MDTypography variant="h6" component="span">
                              Permission
                            </MDTypography>
                          </th>
                          <th
                            style={{
                              backgroundColor: theme.palette.background.default || '#bac7db',
                              padding: '12px 16px',
                              width: '100px',
                              textAlign: 'center',
                            }}
                          >
                            <MDTypography variant="h6" component="span">
                              Backtests
                            </MDTypography>
                          </th>
                          <th
                            style={{
                              backgroundColor: theme.palette.background.default || '#bac7db',
                              padding: '12px 16px',
                              width: '150px',
                              textAlign: 'center',
                            }}
                          >
                            <MDTypography variant="h6" component="span">
                              Goals Status
                            </MDTypography>
                          </th>
                          <th
                            style={{
                              backgroundColor: theme.palette.background.default || '#bac7db',
                              borderTopRightRadius: '12px',
                              borderBottomRightRadius: '12px',
                              padding: '12px 16px',
                              width: '300px',
                              textAlign: 'center',
                            }}
                          >
                            <MDTypography variant="h6" component="span">
                              Actions
                            </MDTypography>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((member) => (
                          <tr key={member.id}>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <MDTypography variant="button" fontWeight="medium">
                                {member.fullName || 'N/A'}
                              </MDTypography>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <MDTypography variant="button" fontWeight="medium">
                                {member.email}
                              </MDTypography>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <MDTypography variant="button" fontWeight="medium">
                                {member.teamRole}
                              </MDTypography>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <MDTypography variant="button" fontWeight="medium">
                                {member.teamPermissions?.[team.id] ? 'Granted' : 'Denied'}
                              </MDTypography>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <MDTypography variant="button" fontWeight="medium">
                                {backtestCounts[member.id] || 0}
                              </MDTypography>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              {member.isFlagged ? (
                                <Tooltip
                                  title={
                                    <MDBox>
                                      {member.unmetGoals.map((goal, index) => (
                                        <MDTypography key={index} variant="caption" display="block">
                                          {goal}
                                        </MDTypography>
                                      ))}
                                    </MDBox>
                                  }
                                  placement="top"
                                >
                                  <MDBox display="flex" alignItems="center" justifyContent="center" color="error.main">
                                    <WarningIcon fontSize="small" style={{ color: iconColor }} />
                                    <MDTypography variant="caption" color="error" ml={1}>
                                      Not Meeting Goals
                                    </MDTypography>
                                  </MDBox>
                                </Tooltip>
                              ) : (
                                <MDTypography variant="caption" color="success">
                                  Meeting Goals
                                </MDTypography>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                <MDButton
                                    variant="text"
                                    color="info"
                                    onClick={() => {
                                    setSelectedMember(member);
                                    const newGoals = {
                                        winRate: member.coachGoals?.[team.id]?.winRate || '',
                                        maxDrawdown: member.coachGoals?.[team.id]?.maxDrawdown || '',
                                        minTradesPerMonth: member.coachGoals?.[team.id]?.minTradesPerMonth || '',
                                        maxTradesPerMonth: member.coachGoals?.[team.id]?.maxTradesPerMonth || '',
                                        minRiskRewardRatio: member.coachGoals?.[team.id]?.minRiskRewardRatio || '',
                                        highConfidenceWinRate: member.coachGoals?.[team.id]?.highConfidenceWinRate || '',
                                        minTradeScore: member.coachGoals?.[team.id]?.minTradeScore || '',
                                        minUniqueSymbols: member.coachGoals?.[team.id]?.minUniqueSymbols || '',
                                        maxUniqueSymbols: member.coachGoals?.[team.id]?.maxUniqueSymbols || '',
                                    };
                                    setGoals(newGoals);
                                    setRiskSettings({ stopLoss: member.coachRiskSettings?.[team.id]?.stopLoss || '' });
                                    setGoalsDialogOpen(true);
                                    }}
                                >
                                    Set Goals
                                </MDButton>
                                {/* Add Coach Logs button */}
                                <MDButton
                                    variant="text"
                                    color="info"
                                    onClick={() => {
                                    setSelectedMember(member);
                                    setCoachLogs([]); // Reset logs
                                    setNewLogNote(''); // Reset input
                                    fetchCoachLogs(member.id, team.id); // Fetch logs
                                    setCoachLogsDialogOpen(true); // Open dialog
                                    }}
                                >
                                    Coach Logs
                                </MDButton>
                                {member.teamRole === 'member' && (
                                    <MDButton
                                    variant="text"
                                    color="info"
                                    onClick={() => handlePromoteCoCoach(member.id)}
                                    >
                                    Promote
                                    </MDButton>
                                )}
                                {member.teamRole === 'co-coach' && (
                                    <MDButton
                                    variant="text"
                                    color="warning"
                                    onClick={() => handleDemoteMember(member.id)}
                                    >
                                    Demote
                                    </MDButton>
                                )}
                                {member.teamRole !== 'coach' && (
                                    <MDButton
                                    variant="text"
                                    color="error"
                                    onClick={() => {
                                        setSelectedMember(member);
                                        setRemoveDialogOpen(true);
                                    }}
                                    disabled={member.teamRole === 'coach'}
                                    >
                                    Remove
                                    </MDButton>
                                )}
                                <MDButton
                                    variant="text"
                                    color="info"
                                    onClick={() => handleSelectMember(member)}
                                    disabled={!member.teamPermissions?.[team.id]}
                                >
                                    View Metrics
                                </MDButton>
                                </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Metrics Tab */}
          {tabValue === 2 && (
            <Grid container spacing={3} mt={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<MDTypography variant="h6">Team Metrics</MDTypography>} />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={3}>
                        <TeamWinRateGraphOverall teamId={team.id} userIDs={permittedUserIDs} />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={3}>
                          <TeamProfitFactorGaugeOverall teamId={team.id} userIDs={permittedUserIDs} />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={3}>
                          <TeamRRRatioTrendOverall teamId={team.id} userIDs={permittedUserIDs} />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDBox mb={3}>
                          <TeamConsistencyScoreOverall teamId={team.id} userIDs={permittedUserIDs} />
                        </MDBox>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              {selectedMember && (selectedMember.teamPermissions?.[team.id] || (selectedMember.id === userID && selectedMember.teamRole === 'coach')) && (
                <Grid item xs={12}>
                  <MDBox mb={2} display="flex" justifyContent="center" alignItems="center">
                    <MDTypography variant="h5">
                      Viewing {selectedMember.fullName}’s Metrics (Account: {tradeAccounts.find(acc => acc.id === selectedTradeAccountID)?.name || 'N/A'})
                    </MDTypography>
                  </MDBox>
                  <Card>
                    <CardHeader
                      title={
                        <MDBox display="flex" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
                          <MDTypography variant="h6">{selectedMember.fullName} Metrics</MDTypography>
                          <Select
                            value={selectedTradeAccountID}
                            onChange={handleTradeAccountChange}
                            displayEmpty
                            sx={{
                              minWidth: 150,
                              '& .MuiSelect-select': { padding: '10px 14px' },
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                            }}
                            disabled={tradeAccounts.length === 0}
                          >
                            <MenuItem value="" disabled>Select Account</MenuItem>
                            {tradeAccounts.map((account) => (
                              <MenuItem key={account.id} value={account.id}>
                                {account.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </MDBox>
                      }
                    />
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={3}>
                            <TeamTotalTradesSummary
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={3}>
                            <TeamWinLossStats
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={3}>
                            <TeamMostProfitableSymbols
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={3}>
                            <TeamTradeAnalytics
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12}>
                          <MDBox mb={3}>
                            <TeamWinRateGraph
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12}>
                          <MDBox mb={3}>
                            <TeamEquityGraph
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={3}>
                            <TeamWinRatePieChart
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={3}>
                            <TeamProfitFactorGauge
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={3}>
                            <TeamRRRatioAnalysisChart
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={3}>
                            <TeamRiskOverTimeChart
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={3}>
                            <TeamRunningNetPL
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDBox mb={3}>
                            <TeamScoreGraph
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12}>
                          <MDBox mb={3}>
                            <TeamJournalTable
                              userID={selectedMember.id}
                              tradeAccountID={selectedTradeAccountID}
                            />
                          </MDBox>
                        </Grid>
                        <MDTypography variant="caption">
                          Backtesting Sessions: {backtestCounts[selectedMember.id] || 0}
                        </MDTypography>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}

          {/* Settings Tab */}
          {tabValue === 3 && (
            <Grid container spacing={3} mt={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<MDTypography variant="h6">Team Settings</MDTypography>} />
                  <CardContent>
                    <TextField
                      label="Team Name"
                      value={newTeamName}
                      onChange={(e) => setNewTeamName(e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                    <MDBox mt={2} display="flex" flexDirection="column" alignItems="center">
                      <input
                        accept="image/*"
                        type="file"
                        style={{ display: 'none' }}
                        id="team-logo-upload"
                        onChange={handleLogoUpload}
                      />
                      <label htmlFor="team-logo-upload">
                        <MDButton variant="outlined" color="info" size="small" component="span">
                          Choose Team Logo
                        </MDButton>
                      </label>
                      {logoUrl && (
                        <Avatar
                          src={logoUrl}
                          alt="Team Logo"
                          sx={{ width: 155, height: 155, marginTop: 2 }}
                        />
                      )}
                    </MDBox>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={handleUpdateSettings}
                      sx={{ mt: 2 }}
                    >
                      Save Settings
                    </MDButton>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Dialogs */}
          <Dialog
            open={settingsDialogOpen}
            onClose={() => setSettingsDialogOpen(false)}
            PaperProps={{
              sx: {
                bgcolor: theme.palette.background.default,
              },
            }}
          >
            <DialogTitle>Edit Team Settings</DialogTitle>
            <DialogContent>
              <TextField
                label="Team Name"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                fullWidth
                margin="normal"
              />
              <MDBox mt={2} display="flex" flexDirection="column" alignItems="center">
                <input
                  accept="image/*"
                  type="file"
                  style={{ display: 'none' }}
                  id="team-logo-upload-dialog"
                  onChange={handleLogoUpload}
                />
                <label htmlFor="team-logo-upload-dialog">
                  <MDButton variant="outlined" color="info" size="small" component="span">
                    Choose Team Logo
                  </MDButton>
                </label>
                {logoUrl && (
                  <Avatar
                    src={logoUrl}
                    alt="Team Logo"
                    sx={{ width: 155, height: 155, marginTop: 2 }}
                  />
                )}
              </MDBox>
            </DialogContent>
            <DialogActions>
              <MDButton onClick={() => setSettingsDialogOpen(false)}>Cancel</MDButton>
              <MDButton onClick={handleUpdateSettings}>Save</MDButton>
            </DialogActions>
          </Dialog>

          <Dialog
  open={goalsDialogOpen}
  onClose={() => {
    setGoalsDialogOpen(false);
    setGoals({ winRate: '', maxDrawdown: '', minTradesPerMonth: '' });
    setRiskSettings({ stopLoss: '' });
  }}
  PaperProps={{
    sx: {
      bgcolor: theme.palette.background.default,
    },
  }}
>
  <DialogTitle>Edit Goals and Risk for {selectedMember?.fullName}</DialogTitle>
  <DialogContent>
    <MDTypography variant="body2" color="text" mb={2}>
      Set performance goals and risk limits for this member. Leave fields blank to unset.
    </MDTypography>
    <TextField
      label="Win Rate (%)"
      type="number"
      value={goals.winRate}
      onChange={(e) => setGoals({ ...goals, winRate: e.target.value })}
      fullWidth
      margin="normal"
      placeholder="e.g., 70"
    />
    <TextField
      label="Max Drawdown (%)"
      type="number"
      value={goals.maxDrawdown}
      onChange={(e) => setGoals({ ...goals, maxDrawdown: e.target.value })}
      fullWidth
      margin="normal"
      placeholder="e.g., 10"
    />
    <TextField
      label="Min Trades/Month"
      type="number"
      value={goals.minTradesPerMonth}
      onChange={(e) => setGoals({ ...goals, minTradesPerMonth: e.target.value })}
      fullWidth
      margin="normal"
      placeholder="e.g., 20"
    />
    <TextField
      label="Max Trades/Month"
      type="number"
      value={goals.maxTradesPerMonth}
      onChange={(e) => setGoals({ ...goals, maxTradesPerMonth: e.target.value })}
      fullWidth
      margin="normal"
      placeholder="e.g., 20"
    />    
    <TextField
      label="Avg Risk Per Trade (%)"
      type="number"
      value={riskSettings.stopLoss}
      onChange={(e) => setRiskSettings({ stopLoss: e.target.value })}
      fullWidth
      margin="normal"
      placeholder="e.g., 2"
      InputProps={{
        endAdornment: (
          <Tooltip title="Calculated from users capital and risk amount per trade">
            <InfoIcon sx={{ color: theme.palette.grey[500] }} />
          </Tooltip>
        ),
      }}
    />
<TextField
  label="Min Risk-to-Reward Ratio"
  type="number"
  value={goals.minRiskRewardRatio}
  onChange={(e) => setGoals({ ...goals, minRiskRewardRatio: e.target.value })}
  fullWidth
  margin="normal"
  placeholder="e.g., 2"
  InputProps={{
    endAdornment: (
      <Tooltip title="Calculated from closed winning trades using riskAmount and returns. May not reflect intended ratio at entry.">
        <InfoIcon sx={{ color: theme.palette.grey[500] }} />
      </Tooltip>
    ),
  }}
/>
<TextField
  label="High-Confidence Win Rate (%)"
  type="number"
  value={goals.highConfidenceWinRate}
  onChange={(e) => setGoals({ ...goals, highConfidenceWinRate: e.target.value })}
  fullWidth
  margin="normal"
  placeholder="e.g., 70"
  InputProps={{
    endAdornment: (
      <Tooltip title="Calculated win rate from high confidence trades.">
        <InfoIcon sx={{ color: theme.palette.grey[500] }} />
      </Tooltip>
    ),
  }}
/>
<TextField
  label="Min Trade Score"
  type="number"
  value={goals.minTradeScore}
  onChange={(e) => setGoals({ ...goals, minTradeScore: e.target.value })}
  fullWidth
  margin="normal"
  placeholder="e.g., 70"
  InputProps={{
    endAdornment: (
      <Tooltip title="Calculated from traders checklist, ensuring trader is adhering to their strategy.">
        <InfoIcon sx={{ color: theme.palette.grey[500] }} />
      </Tooltip>
    ),
  }}
/>
<TextField
  label="Min Unique Symbols"
  type="number"
  value={goals.minUniqueSymbols}
  onChange={(e) => setGoals({ ...goals, minUniqueSymbols: e.target.value })}
  fullWidth
  margin="normal"
  placeholder="e.g., 3"
  InputProps={{
    endAdornment: (
      <Tooltip title="Set a minimum amount of symbols a trader should be looking at, to diversify.">
        <InfoIcon sx={{ color: theme.palette.grey[500] }} />
      </Tooltip>
    ),
  }}
/>
<TextField
  label="Max Unique Symbols"
  type="number"
  value={goals.maxUniqueSymbols}
  onChange={(e) => setGoals({ ...goals, maxUniqueSymbols: e.target.value })}
  fullWidth
  margin="normal"
  placeholder="e.g., 3"
  InputProps={{
    endAdornment: (
      <Tooltip title="Set a maximum amount of symbols a trader should be looking at, to focus trading activity.">
        <InfoIcon sx={{ color: theme.palette.grey[500] }} />
      </Tooltip>
    ),
  }}
/>
  </DialogContent>
  <DialogActions>
  <MDButton
    onClick={() => {
      setGoalsDialogOpen(false);
      setGoals({
        winRate: '',
        maxDrawdown: '',
        minTradesPerMonth: '',
        maxTradesPerMonth: '',
        minRiskRewardRatio: '',
        highConfidenceWinRate: '',
        minTradeScore: '',
        minUniqueSymbols: '',
        maxUniqueSymbols: '',
      });
      setRiskSettings({ stopLoss: '' });
    }}
  >
    Cancel
  </MDButton>
  <MDButton
    onClick={async () => {
      try {
        // Parse all goal values
        const goalValues = {
          winRate: goals.winRate ? Number(goals.winRate) : null,
          maxDrawdown: goals.maxDrawdown ? Number(goals.maxDrawdown) : null,
          minTradesPerMonth: goals.minTradesPerMonth ? Number(goals.minTradesPerMonth) : null,
          maxTradesPerMonth: goals.maxTradesPerMonth ? Number(goals.maxTradesPerMonth) : null,
          minRiskRewardRatio: goals.minRiskRewardRatio ? Number(goals.minRiskRewardRatio) : null,
          highConfidenceWinRate: goals.highConfidenceWinRate ? Number(goals.highConfidenceWinRate) : null,
          minTradeScore: goals.minTradeScore ? Number(goals.minTradeScore) : null,
          minUniqueSymbols: goals.minUniqueSymbols ? Number(goals.minUniqueSymbols) : null,
          maxUniqueSymbols: goals.maxUniqueSymbols ? Number(goals.maxUniqueSymbols) : null,
          stopLoss: riskSettings.stopLoss ? Number(riskSettings.stopLoss) : null,
        };

        // Validate non-negative numbers and logical constraints
        for (const [key, value] of Object.entries(goalValues)) {
          if (value !== null && (isNaN(value) || value < 0)) {
            setSnackbarMessage(`${key} must be a non-negative number`);
            setSnackbarOpen(true);
            return;
          }
        }
        if (goalValues.minTradesPerMonth !== null && goalValues.maxTradesPerMonth !== null) {
          if (goalValues.minTradesPerMonth > goalValues.maxTradesPerMonth) {
            setSnackbarMessage('Min Trades/Month cannot exceed Max Trades/Month');
            setSnackbarOpen(true);
            return;
          }
        }
        if (goalValues.minUniqueSymbols !== null && goalValues.maxUniqueSymbols !== null) {
          if (goalValues.minUniqueSymbols > goalValues.maxUniqueSymbols) {
            setSnackbarMessage('Min Unique Symbols cannot exceed Max Unique Symbols');
            setSnackbarOpen(true);
            return;
          }
        }
        if (goalValues.minRiskRewardRatio !== null && goalValues.minRiskRewardRatio < 1) {
          setSnackbarMessage('Min Risk-to-Reward Ratio should be at least 1');
          setSnackbarOpen(true);
          return;
        }
        if (goalValues.highConfidenceWinRate !== null && (goalValues.highConfidenceWinRate > 100 || goalValues.highConfidenceWinRate < 0)) {
          setSnackbarMessage('High-Confidence Win Rate must be between 0 and 100');
          setSnackbarOpen(true);
          return;
        }
        if (goalValues.minTradeScore !== null && (goalValues.minTradeScore > 100 || goalValues.minTradeScore < 0)) {
          setSnackbarMessage('Min Trade Score must be between 0 and 100');
          setSnackbarOpen(true);
          return;
        }

        // Update goals if any goal field is set
        const hasGoals = Object.values(goalValues).some(val => val !== null && val !== goalValues.stopLoss);
        if (hasGoals) {
          await updateDoc(doc(db, 'Users', selectedMember.id), {
            [`profileInfo.coachGoals.${team.id}`]: {
              winRate: goalValues.winRate,
              maxDrawdown: goalValues.maxDrawdown,
              minTradesPerMonth: goalValues.minTradesPerMonth,
              maxTradesPerMonth: goalValues.maxTradesPerMonth,
              minRiskRewardRatio: goalValues.minRiskRewardRatio,
              highConfidenceWinRate: goalValues.highConfidenceWinRate,
              minTradeScore: goalValues.minTradeScore,
              minUniqueSymbols: goalValues.minUniqueSymbols,
              maxUniqueSymbols: goalValues.maxUniqueSymbols,
              setBy: userID,
              setAt: new Date(),
            },
          });
        }

        // Update risk settings if stopLoss is set
        if (goalValues.stopLoss !== null) {
          await updateDoc(doc(db, 'Users', selectedMember.id), {
            [`profileInfo.coachRiskSettings.${team.id}`]: {
              stopLoss: goalValues.stopLoss,
              setBy: userID,
              setAt: new Date(),
            },
          });
        }

        // Update local state
        setMembers(
          members.map((m) =>
            m.id === selectedMember.id
              ? {
                  ...m,
                  coachGoals: {
                    ...m.coachGoals,
                    [team.id]: {
                      winRate: goalValues.winRate,
                      maxDrawdown: goalValues.maxDrawdown,
                      minTradesPerMonth: goalValues.minTradesPerMonth,
                      maxTradesPerMonth: goalValues.maxTradesPerMonth,
                      minRiskRewardRatio: goalValues.minRiskRewardRatio,
                      highConfidenceWinRate: goalValues.highConfidenceWinRate,
                      minTradeScore: goalValues.minTradeScore,
                      minUniqueSymbols: goalValues.minUniqueSymbols,
                      maxUniqueSymbols: goalValues.maxUniqueSymbols,
                      setBy: userID,
                      setAt: new Date(),
                    },
                  },
                  coachRiskSettings: {
                    ...m.coachRiskSettings,
                    [team.id]: {
                      stopLoss: goalValues.stopLoss,
                      setBy: userID,
                      setAt: new Date(),
                    },
                  },
                }
              : m
          )
        );

        setGoalsDialogOpen(false);
        setGoals({
          winRate: '',
          maxDrawdown: '',
          minTradesPerMonth: '',
          maxTradesPerMonth: '',
          minRiskRewardRatio: '',
          highConfidenceWinRate: '',
          minTradeScore: '',
          minUniqueSymbols: '',
          maxUniqueSymbols: '',
        });
        setRiskSettings({ stopLoss: '' });
        setSnackbarMessage('Goals and risk settings saved successfully');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Error saving goals and risk settings:', error);
        setSnackbarMessage('Failed to save goals and risk settings');
        setSnackbarOpen(true);
      }
    }}
    disabled={
      !goals.winRate &&
      !goals.maxDrawdown &&
      !goals.minTradesPerMonth &&
      !goals.maxTradesPerMonth &&
      !goals.minRiskRewardRatio &&
      !goals.highConfidenceWinRate &&
      !goals.minTradeScore &&
      !goals.minUniqueSymbols &&
      !goals.maxUniqueSymbols &&
      !riskSettings.stopLoss
    }
    variant="contained"
    color="success"
  >
    Save
  </MDButton>
</DialogActions>
</Dialog>

          <Dialog
            open={removeDialogOpen}
            onClose={() => setRemoveDialogOpen(false)}
            PaperProps={{
              sx: {
                bgcolor: theme.palette.background.default,
              },
            }}
          >
            <DialogTitle>Remove {selectedMember?.fullName}</DialogTitle>
            <DialogContent>
              <MDTypography variant="body2">
                Are you sure you want to remove this member? They will revert to a standard monthly plan.
              </MDTypography>
            </DialogContent>
            <DialogActions>
              <MDButton onClick={() => setRemoveDialogOpen(false)}>Cancel</MDButton>
              <MDButton onClick={handleRemoveMember} color="error">
                Remove
              </MDButton>
            </DialogActions>
          </Dialog>
          {/* New Add Member Dialog */}
          <Dialog
                open={addMemberDialogOpen}
                onClose={() => {
                    setAddMemberDialogOpen(false);
                    setNewMemberEmail('');
                    setAddMemberError('');
                }}
                PaperProps={{
                    sx: {
                    bgcolor: theme.palette.background.default,
                    },
                }}
                >
                <DialogTitle>Add Team Member</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" mb={2}>
                    Enter the email address of the user to add to the team. They will be added as a member and prompted to grant data access.
                    </MDTypography>
                    <TextField
                    label="Email Address"
                    type="email"
                    value={newMemberEmail}
                    onChange={(e) => {
                        setNewMemberEmail(e.target.value);
                        setAddMemberError('');
                    }}
                    fullWidth
                    margin="normal"
                    error={!!addMemberError}
                    helperText={addMemberError}
                    />
                </DialogContent>
                <DialogActions>
                    <MDButton
                    onClick={() => {
                        setAddMemberDialogOpen(false);
                        setNewMemberEmail('');
                        setAddMemberError('');
                    }}
                    >
                    Cancel
                    </MDButton>
                    <MDButton
                    onClick={handleAddMember}
                    color="info"
                    disabled={!newMemberEmail || addingMember}
                    >
                    {addingMember ? <CircularProgress size={20} /> : 'Add Member'}
                    </MDButton>
                </DialogActions>
                </Dialog>
                <Dialog
  open={coachLogsDialogOpen}
  onClose={() => {
    setCoachLogsDialogOpen(false);
    setNewLogNote('');
    setCoachLogs([]);
    setEditingLogId(null);
    setEditedLogNote('');
    setDeleteLogDialogOpen(false);
    setLogToDelete(null);
  }}
  PaperProps={{
    sx: {
      bgcolor: theme.palette.background.default,
      width: '100%',
      maxWidth: 600,
      p: 2,
    },
  }}
>
  <DialogTitle>Coach Logs for {selectedMember?.fullName}</DialogTitle>
  <DialogContent>
    <MDTypography variant="body2" mb={2}>
      Add notes from your weekly check-ins. These notes are visible to the member.
    </MDTypography>
    <MDBox
      sx={{
        bgcolor: theme.palette.background.default,
        p: 2,
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        mb: 3,
      }}
    >
      <MDTypography variant="subtitle2" mb={1}>
        Coach Log Note
      </MDTypography>
      <TextareaAutosize
        minRows={4}
        value={newLogNote}
        onChange={(e) => setNewLogNote(e.target.value)}
        placeholder="Enter your notes here..."
        style={{
          width: '100%',
          padding: '8px',
          borderRadius: '4px',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
          fontFamily: theme.typography.body2.fontFamily,
          fontSize: theme.typography.body2.fontSize,
          color: iconColor, // Set text color to theme's primary text
          resize: 'vertical',
        }}
      />
    </MDBox>
    <MDButton
      variant="contained"
      color="info"
      onClick={handleSaveCoachLog}
      disabled={savingLog || !newLogNote.trim()}
      sx={{ mb: 3, width: '100%' }}
    >
      {savingLog ? <CircularProgress size={20} /> : 'Save Log'}
    </MDButton>
    {logsLoading ? (
      <MDBox display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </MDBox>
    ) : coachLogs.length > 0 ? (
      coachLogs.map((log) => (
        <Accordion key={log.id} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <MDTypography variant="subtitle2">
              {log.createdAt.toLocaleString()} by {log.createdBy === userID ? 'You' : 'Coach'}
              {log.updatedAt && (
                <MDTypography variant="caption" color="textSecondary" ml={1}>
                  (Edited {log.updatedAt.toLocaleString()})
                </MDTypography>
              )}
            </MDTypography>
          </AccordionSummary>
          <AccordionDetails>
            {editingLogId === log.id ? (
              <MDBox
                sx={{
                    bgcolor: theme.palette.background.default,
                  p: 2,
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <MDTypography variant="subtitle2" mb={1}>
                  Edit Coach Log Note
                </MDTypography>
                <TextareaAutosize
                  minRows={4}
                  value={editedLogNote}
                  onChange={(e) => setEditedLogNote(e.target.value)}
                  placeholder="Edit your note here..."
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.default,
                    fontFamily: theme.typography.body2.fontFamily,
                    fontSize: theme.typography.body2.fontSize,
                    resize: 'vertical',
                    color: iconColor, // Set text color to theme's primary text
                  }}
                />
                <MDBox mt={2} display="flex" gap={1}>
                  <MDButton
                    variant="contained"
                    color="success"
                    onClick={() => handleSaveEditedCoachLog(log.id)}
                    disabled={savingLog || !editedLogNote.trim()}
                  >
                    {savingLog ? <CircularProgress size={20} /> : 'Save Edit'}
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setEditingLogId(null);
                      setEditedLogNote('');
                    }}
                  >
                    Cancel
                  </MDButton>
                </MDBox>
              </MDBox>
            ) : (
              <MDBox>
                <MDTypography variant="body2">{log.note}</MDTypography>
                <MDBox mt={2} display="flex" gap={1}>
                  <MDButton
                    variant="text"
                    color="info"
                    onClick={() => handleEditCoachLog(log.id, log.note)}
                  >
                    Edit
                  </MDButton>
                  <MDButton
                    variant="text"
                    color="error"
                    onClick={() => handleOpenDeleteLogDialog(log.id)}
                  >
                    Delete
                  </MDButton>
                </MDBox>
              </MDBox>
            )}
          </AccordionDetails>
        </Accordion>
      ))
    ) : (
      <MDTypography variant="body2" color="textSecondary">
        No logs available.
      </MDTypography>
    )}
  </DialogContent>
  <DialogActions>
    <MDButton
      onClick={() => {
        setCoachLogsDialogOpen(false);
        setNewLogNote('');
        setCoachLogs([]);
        setEditingLogId(null);
        setEditedLogNote('');
        setDeleteLogDialogOpen(false);
        setLogToDelete(null);
      }}
    >
      Close
    </MDButton>
  </DialogActions>
</Dialog>

{/* Delete Confirmation Dialog */}
<Dialog
  open={deleteLogDialogOpen}
  onClose={() => {
    setDeleteLogDialogOpen(false);
    setLogToDelete(null);
  }}
  PaperProps={{
    sx: {
      bgcolor: theme.palette.background.default,
    },
  }}
>
  <DialogTitle>Delete Coach Log</DialogTitle>
  <DialogContent>
    <MDTypography variant="body2">
      Are you sure you want to delete this coach log? This action cannot be undone.
    </MDTypography>
  </DialogContent>
  <DialogActions>
    <MDButton
      onClick={() => {
        setDeleteLogDialogOpen(false);
        setLogToDelete(null);
      }}
    >
      Cancel
    </MDButton>
    <MDButton
      onClick={handleDeleteCoachLog}
      color="error"
    >
      Delete
    </MDButton>
  </DialogActions>
</Dialog>

{/* Delete Confirmation Dialog */}
<Dialog
  open={deleteLogDialogOpen}
  onClose={() => {
    setDeleteLogDialogOpen(false);
    setLogToDelete(null);
  }}
  PaperProps={{
    sx: {
      bgcolor: theme.palette.background.default,
    },
  }}
>
  <DialogTitle>Delete Coach Log</DialogTitle>
  <DialogContent>
    <MDTypography variant="body2">
      Are you sure you want to delete this coach log? This action cannot be undone.
    </MDTypography>
  </DialogContent>
  <DialogActions>
    <MDButton
      onClick={() => {
        setDeleteLogDialogOpen(false);
        setLogToDelete(null);
      }}
    >
      Cancel
    </MDButton>
    <MDButton
      onClick={handleDeleteCoachLog}
      color="error"
    >
      Delete
    </MDButton>
  </DialogActions>
</Dialog>

{/* Delete Confirmation Dialog */}
<Dialog
  open={deleteLogDialogOpen}
  onClose={() => {
    setDeleteLogDialogOpen(false);
    setLogToDelete(null);
  }}
  PaperProps={{
    sx: {
      bgcolor: theme.palette.background.default,
    },
  }}
>
  <DialogTitle>Delete Coach Log</DialogTitle>
  <DialogContent>
    <MDTypography variant="body2">
      Are you sure you want to delete this coach log? This action cannot be undone.
    </MDTypography>
  </DialogContent>
  <DialogActions>
    <MDButton
      onClick={() => {
        setDeleteLogDialogOpen(false);
        setLogToDelete(null);
      }}
    >
      Cancel
    </MDButton>
    <MDButton
      onClick={handleDeleteCoachLog}
      color="error"
    >
      Delete
    </MDButton>
  </DialogActions>
</Dialog>
                <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                >
                <Alert onClose={() => setSnackbarOpen(false)} severity="success">
                    {snackbarMessage}
                </Alert>
                </Snackbar>
        </MDBox>
      </PeriodProvider>
      <Footer />
    </DashboardLayout>
  );
};

TeamDashboard.propTypes = {
  userID: PropTypes.string.isRequired,
  profileInfo: PropTypes.object.isRequired,
};

export default TeamDashboard;